import { useState, type FormEvent } from 'react'
import { useMe } from '../api/me'
import { RequestFailed } from '../api/client'
import { useInviteTeamMember, useTeam, useUpdateTeamMember, type TeamMember } from '../api/team'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'

type InviteRole = 'admin' | 'agent' | 'viewer'

const roleLabel: Record<TeamMember['role'], string> = { owner: 'Owner', admin: 'Admin', agent: 'Agent', viewer: 'Viewer' }
const roleHint: Record<InviteRole, string> = {
  admin: 'Everything except billing and the WhatsApp connection.',
  agent: 'The inbox: conversations assigned to them or unassigned, contacts, templates.',
  viewer: 'Read-only: the inbox, contacts, campaigns.',
}

// Who is on the team, and adding or changing members. Owners and admins
// see this page; the server decides who may change whom.
export function TeamPage() {
  const me = useMe()
  const team = useTeam()
  const invite = useInviteTeamMember()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<InviteRole>('agent')
  const myRole = me.data?.user.role
  const owner = myRole === 'owner'
  const error = invite.error as RequestFailed | null
  const fieldError = (k: string) => error?.fields?.[k]

  const submit = (e: FormEvent) => {
    e.preventDefault()
    invite.mutate(
      { name: name.trim(), email: email.trim(), role },
      {
        onSuccess: () => {
          setName('')
          setEmail('')
        },
      },
    )
  }

  return (
    <main className="mx-auto flex max-w-2xl flex-col gap-6 px-4 py-6 sm:py-8">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">Team</h1>
        <p className="text-sm text-muted-foreground">
          Teammates sign in with a code emailed to them; there is no password to share. Disabling someone signs them out within a minute.
        </p>
      </div>

      <form className="flex flex-col gap-3 rounded-xl border bg-card p-4" onSubmit={submit}>
        <h2 className="font-semibold">Add a teammate</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-name">Name</Label>
            <Input id="invite-name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
            {fieldError('name') && <p className="text-sm text-destructive">{fieldError('name')}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input id="invite-email" type="email" maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} />
            {fieldError('email') && <p className="text-sm text-destructive">{fieldError('email')}</p>}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite-role">Role</Label>
          <Select value={role} onValueChange={(v) => setRole(v as InviteRole)}>
            <SelectTrigger id="invite-role" className="w-full sm:w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="agent">Agent</SelectItem>
              <SelectItem value="viewer">Viewer</SelectItem>
              {owner && <SelectItem value="admin">Admin</SelectItem>}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{roleHint[role]}{!owner && ' Only the owner can add admins.'}</p>
          {fieldError('role') && <p className="text-sm text-destructive">{fieldError('role')}</p>}
        </div>
        {error && !error.fields && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
        {invite.isSuccess && <p role="status" className="text-sm text-primary-text">Added {invite.data.name}. They have an email saying how to sign in.</p>}
        <div>
          <Button type="submit" disabled={!name.trim() || !email.trim() || invite.isPending}>{invite.isPending ? 'Adding' : 'Add teammate'}</Button>
        </div>
      </form>

      {team.isPending ? (
        <div role="status" aria-label="Loading the team" className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
        </div>
      ) : team.isError ? (
        <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(team.error as RequestFailed).message}</p>
      ) : (
        <ul className="divide-y rounded-xl border bg-card">
          {team.data.map((m) => (
            <MemberRow key={m.id} member={m} myId={me.data?.user.id} owner={owner} />
          ))}
        </ul>
      )}
    </main>
  )
}

function MemberRow({ member, myId, owner }: { member: TeamMember; myId: string | undefined; owner: boolean }) {
  const update = useUpdateTeamMember(member.id)
  const error = update.error as RequestFailed | null
  // What the signed-in person may change, mirroring the server's rule.
  const manageable = member.id !== myId && member.role !== 'owner' && (owner || member.role === 'agent' || member.role === 'viewer')
  const disabled = member.status === 'disabled'

  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="min-w-0 flex-[1_1_200px]">
          <span className="block truncate font-semibold">
            {member.name}
            {member.id === myId && <span className="font-normal text-muted-foreground"> (you)</span>}
          </span>
          <span className="block truncate text-sm text-muted-foreground">{member.email}</span>
        </span>
        {disabled && <Badge variant="neutral">Disabled</Badge>}
        {manageable ? (
          <>
            <Select value={member.role} disabled={update.isPending} onValueChange={(v) => update.mutate({ role: v as InviteRole })}>
              <SelectTrigger aria-label={`Role of ${member.name}`} className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="agent">Agent</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
                {owner && <SelectItem value="admin">Admin</SelectItem>}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant={disabled ? 'outline' : 'ghost'}
              className={disabled ? '' : 'text-destructive'}
              disabled={update.isPending}
              onClick={() => update.mutate({ status: disabled ? 'active' : 'disabled' })}
            >
              {update.isPending ? 'Saving' : disabled ? 'Re-enable' : 'Disable'}
            </Button>
          </>
        ) : (
          <Badge variant="outline">{roleLabel[member.role]}</Badge>
        )}
      </div>
      {error && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
    </li>
  )
}
